import React, { useState } from "react";
import "./CustomTable.css";

interface Column {
    id: string;
    label: string;
    minWidth?: number;
    align?: "right" | "left" | "center";
}

interface CustomTableProps {
    columns: Column[];
    rows: Record<string, any>[];
    totalCount: number;
    currentPage: number;
    pageSize: number;
    onPageChange: (page: number) => void;
    onRowsReorder?: (newRows: Record<string, any>[]) => void;
    draggable?: boolean;
}

const getAlignClass = (align?: "right" | "left" | "center") => {
    switch (align) {
        case "right":
            return "text-end";
        case "center":
            return "text-center";
        default:
            return "text-start";
    }
};

const CustomTable: React.FC<CustomTableProps> = ({
    columns,
    rows,
    totalCount,
    currentPage,
    pageSize,
    onPageChange,
    onRowsReorder,
    draggable = false,
}) => {
    const [draggedIdx, setDraggedIdx] = useState<number | null>(null);
    const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
    const totalPages = Math.ceil(totalCount / pageSize);

    const handleDragStart = (idx: number, e: React.DragEvent) => {
        setDraggedIdx(idx);
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", idx.toString()); // Required
    };

    const handleDragOver = (idx: number, e: React.DragEvent) => {
        e.preventDefault(); // Required to allow dropping
        setDragOverIdx(idx);
    };

    const handleDrop = (idx: number, e: React.DragEvent) => {
        e.preventDefault();
        const draggedIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);

        if (draggedIndex !== idx && onRowsReorder) {
            const newRows = [...rows];
            const [removed] = newRows.splice(draggedIndex, 1);
            newRows.splice(idx, 0, removed);
            onRowsReorder(newRows);
        }

        setDraggedIdx(null);
        setDragOverIdx(null);
    };

    const handleDragEnd = () => {
        setDraggedIdx(null);
        setDragOverIdx(null);
    };

    return (
        <div className="table-container">
            <table className="align-middle custom-table">
                <thead className="table-dark">
                    <tr>
                        {columns.map((column) => (
                            <th
                                key={column.id}
                                className={getAlignClass(column.align)}
                                style={{ minWidth: column.minWidth || 100 }}
                            >
                                {column.label}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.length === 0 ? (
                        <tr>
                            <td colSpan={columns.length} className="text-center">
                                No data available.
                            </td>
                        </tr>
                    ) : (
                        rows.map((row, idx) => (
                            <tr
                                key={idx}
                                draggable={draggable}
                                onDragStart={draggable ? (e) => handleDragStart(idx, e) : undefined}
                                onDragOver={draggable ? (e) => handleDragOver(idx, e) : undefined}
                                onDrop={draggable ? (e) => handleDrop(idx, e) : undefined}
                                onDragEnd={draggable ? handleDragEnd : undefined}
                                className={
                                    draggable && draggedIdx === idx
                                        ? "dragging-row"
                                        : draggable && dragOverIdx === idx
                                        ? "drag-over-row"
                                        : "table-row"
                                }
                            >
                                {columns.map((column) => (
                                    <td
                                        key={column.id}
                                        className={getAlignClass(column.align)}
                                    >
                                        {row[column.id]}
                                    </td>
                                ))}
                            </tr>
                        ))
                    )}
                </tbody>
            </table>
            <div className="pagination-container">
                <button
                    disabled={currentPage === 1}
                    onClick={() => onPageChange(currentPage - 1)}
                    className="page-btn"
                >
                    Previous
                </button>
                <span>
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages}
                    onClick={() => onPageChange(currentPage + 1)}
                    className="page-btn"
                >
                    Next
                </button>
            </div>
        </div>
    );
};

export default CustomTable;
